import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { AddAttachmentDialogComponent } from '../add-attachment-dialog/add-attachment-dialog.component';
import { TylerMatTableDataSource } from '../angular-material/extended.class';
import { GeneralConfirmationComponent } from '../controls/general-confirmation/general-confirmation.component';
import { Attachment } from '../models/attachment.model';
import { SharedService } from '../shared.service';
import { Student } from '../student/student.model';
import { StudentService } from '../student/student.service';
import { AttendanceAdministrationService } from '../../admin360/attendance-administration/attendance-administration.service';
import { AttendanceCheckInOut } from '../../admin360/attendance-administration/attendance-check-in-out/attendance-check-in-out.model';
import { MatSort } from '@angular/material/sort';
import { SelfCheckInOutHistoryDialogComponent } from '../self-check-in-out-history-dialog/self-check-in-out-history-dialog.component';
import { AttendanceCheckInOutLegendComponent } from '../attendance-check-in-out-legend/attendance-check-in-out-legend.component';
import { AttendanceByStudentComponent } from '../../admin360/attendance-administration/attendance-by-student/attendance-by-student.component';
import { ViewAttachmentsComponent } from '../view-attachments/view-attachments.component';
import { UserService } from '../user/user.service';

@Component({
  selector: 'app-check-in-out-view-history-dialog',
  templateUrl: './check-in-out-view-history-dialog.component.html',
  styleUrls: ['./check-in-out-view-history-dialog.component.css']
})
export class CheckInOutViewHistoryDialogComponent implements OnInit {
  studentId: number;
  schoolId: number;
  checkInOutHistory: AttendanceCheckInOut[] = [];

  columnsToDisplay = ['Type', 'Date', 'Time', 'Status', 'Period', 'Code', 'LEMinutes', 'Attachment', 'Actions'];
  angularMatData = new TylerMatTableDataSource(this.checkInOutHistory);

  _sort: MatSort;
  useStudents: Student[] = [];
  selectedStudent: Student;
  isStaff = false;

  constructor(private dialogRef: MatDialogRef<CheckInOutViewHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private attendanceAdminService: AttendanceAdministrationService,
    private sharedService: SharedService,
    private studentService: StudentService,
    private userService: UserService) {
      this.useStudents.push(this.data.selectedStudent);
     }

  @ViewChild(MatSort) set sort(value: MatSort) {
    this._sort = value;
    this.angularMatData.sort = value;
  }

  get sort(): MatSort {
    return this._sort;
  }

  ngOnInit() {
    this.studentId = this.data.selectedStudent.Id;
    this.schoolId = this.data.schoolId;
    this.isStaff = this.userService.currentUser.UserType == 1 ? true : false;
    if (this.isStaff == false) {
      this.columnsToDisplay = ['Type', 'Date', 'Time', 'Status', 'Period', 'Code', 'LEMinutes', 'Attachment'];
    }
    this.getCheckInOutHistory();
  }

  getCheckInOutHistory() {
    this.attendanceAdminService.getAttendanceCheckInOutHistoryByStudentId(this.schoolId, this.studentId).pipe(take(1)).subscribe(history => {
      this.checkInOutHistory = history;
      this.angularMatData.data = this.checkInOutHistory;
    });
  }

  cancelClick() {
    this.dialogRef.close();
  }

  deleteClick(recordId: number, type: string) {
    const date = this.checkInOutHistory.find(o => o.HistoryId == recordId).Date;
    const deleteConfirmDialog = this.dialog.open(GeneralConfirmationComponent, {
      panelClass: 'tyl-mat-dialog',
      disableClose: true,
    });
    deleteConfirmDialog.componentInstance.headerLabel = 'Delete Check In/Out Record';
    deleteConfirmDialog.componentInstance.confirmMessage = 'Are you sure you want to delete this Check In/Check Out Record?';
    deleteConfirmDialog.componentInstance.okButtonLabel = 'Delete';
    deleteConfirmDialog.componentInstance.confirmEvent.subscribe((deleteObject: boolean) => {
      if (deleteObject) {
        this.attendanceAdminService.deleteCheckInOutRecordById(recordId, type).pipe(take(1)).subscribe(result => {
          if (result == true) {
            this.getCheckInOutHistory();
            const informationDialog = this.dialog.open(GeneralConfirmationComponent, {
              panelClass: 'tyl-mat-dialog',
              disableClose: true,
              maxWidth: '40vw',
              minWidth: '40vw'
            });
            informationDialog.componentInstance.headerLabel = 'Record Deleted';
            informationDialog.componentInstance.confirmMessage = 'The Check In (or Check Out) record has been deleted.  It is recommended you visit this students attendance for this date ' + date + ' to correct/review their attendance.';
            informationDialog.componentInstance.disableConfirmation = true;
            informationDialog.componentInstance.informational = true;
          }
          else {
            this.sharedService.showBadSnackbar();
          }
        });
      }
    });
  }

  addAttachmentClick(historyId: number) {
    // POST: opens a modal to add an attachment

    // Initializing attachment
    const attachment = new Attachment();
    attachment.Id = -1;
    attachment.DocumentId = 0;
    attachment.FileName = '';
    attachment.Description = '';
    attachment.UploadedDate = new Date();
    attachment.MyFileBase64String = '';
    attachment.FileType = '';
    attachment.Comment = '';
    attachment.ViewFileOnWebsite = true;
    attachment.AttachmentDescription = '';

    // Opening modal
    const dialogBox = this.dialog.open<AddAttachmentDialogComponent>(AddAttachmentDialogComponent, {
      data: {
        attachment: attachment,
        newAttachment: true,
        requireDocumentType: true
      },
      disableClose: true
    });
    dialogBox.afterClosed().subscribe(() => {
      if (dialogBox.componentInstance.saved) {
        const attachmentData: Attachment[] = [];
        attachmentData.push(attachment);
        this.saveAttachment(historyId, attachmentData);
      }
    });
  }

  saveAttachment(historyId: number, attachment: Attachment[]) {
    attachment.forEach(att => {
      att.AttachmentDescription = att.Description;
      att.DocumentBase64 = att.MyFileBase64String;
      att.MyFileBase64String = '';
    });
    this.attendanceAdminService.saveCheckInOutHistoryAttachment(historyId, attachment).pipe(take(1)).subscribe(result => {
      if (result == true) {
        this.getCheckInOutHistory();
      }
    });
  }

  viewSelfCheckHistoryClick(calledFromMenu = false) {
    const dialogBox = this.dialog.open(SelfCheckInOutHistoryDialogComponent, {
      disableClose: true,
      minWidth: '80vw',
      maxWidth: '120vw',
      minHeight: '80vh',
      data: {
        studentId: this.studentId,
        calledFromMenu: calledFromMenu,
        selectedStudent: this.data.selectedStudent
      }
    });
  }

  legendClick() {
    const dialogBox = this.dialog.open(AttendanceCheckInOutLegendComponent, {
      disableClose: true,
      maxWidth: '40vw',
      maxHeight: '65vh'
    });
  }

  editClick(attendanceCheckInOut: AttendanceCheckInOut) {
    this.attendanceAdminService.getCheckInOutHistoryAttachmentById(attendanceCheckInOut.HistoryId, 'History').pipe(take(1)).subscribe(attachment => {
      attendanceCheckInOut.Attachments = attachment;
      this.studentService.getStudent(attendanceCheckInOut.StudentId).pipe(take(1)).subscribe(student => {
        this.selectedStudent = student;

        const attendanceByStudentDialog = this.dialog.open(AttendanceByStudentComponent, {
          data: {
            calledAsDialog: true,
            selectedStudent: this.selectedStudent,
            attendanceDate: new Date(attendanceCheckInOut.Date),
            attendanceCheckInOut: attendanceCheckInOut
          },
          disableClose: true,
          minWidth: '90vw',
          maxWidth: '90vw',
          minHeight: '90vh',
          maxHeight: '90vh'
        });

        attendanceByStudentDialog.afterClosed().subscribe(result => {
          if (result != undefined) {
            const attach = result.Attachments.filter(o => o.Id < 0 || o.Deleted == true);
            if (attach.length > 0) {
              this.saveAttachment(result.CheckInOutHistoryId, result.Attachments);
            }
            this.getCheckInOutHistory();
          }
        });
      });
    });
  }

  viwAttachments(attendanceCheckInOut: AttendanceCheckInOut) {
    const id = attendanceCheckInOut.HistoryId != null && attendanceCheckInOut.HistoryId > 0 ? attendanceCheckInOut.HistoryId : attendanceCheckInOut.SubmissionId;
    const type = attendanceCheckInOut.HistoryId != null && attendanceCheckInOut.HistoryId > 0 ? 'History' : 'Submission';
    this.attendanceAdminService.getCheckInOutHistoryAttachmentById(id, type).pipe(take(1)).subscribe(attachment => {
      this.dialog.open(ViewAttachmentsComponent, {
        data: {
          attachments: attachment
        },
        disableClose: true,
        minWidth: '750px'
      });
    });
  }

}
